import { createFileRoute, Link } from '@tanstack/react-router';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { useCustomers } from '@/hooks/useCustomers';
import { useMaintenanceDue } from '@/hooks/useMaintenanceDue';
import { useSystems } from '@/hooks/useSystems';
import { useSyncStatus } from '@/hooks/useSyncStatus';

export const Route = createFileRoute('/')({
  component: DashboardPage,
});

function DashboardPage(): JSX.Element {
  const customers = useCustomers({ limit: 1 });
  const systemsAll = useSystems({ limit: 1 });
  const systemsInScope = useSystems({ limit: 1, scope: 'in_scope' });
  const sync = useSyncStatus();
  const due = useMaintenanceDue();

  const dueMeta = due.data?.meta;
  const dueCount = dueMeta ? dueMeta.overdue + dueMeta.due_soon : null;
  const overdueCount = dueMeta?.overdue ?? 0;

  const customerCount = customers.data?.meta?.total_estimate ?? null;
  const systemCount = systemsAll.data?.meta?.total_estimate ?? null;
  const inScopeCount = systemsInScope.data?.meta?.total_estimate ?? null;

  const lastSync = sync.data?.last_run_at;
  const nextSync = sync.data?.next_run_at;
  const fetched = sync.data?.items_fetched ?? 0;

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold">Dashboard</h1>
        <p className="text-muted">
          Übersicht über Kunden, Systeme und Sync-Status.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card title="Kunden" description="Aktive Einträge im Inventar">
          <p className="text-3xl font-semibold">{formatCount(customerCount)}</p>
          <p className="mt-1 text-xs text-muted">
            <Link to="/customers" className="underline hover:text-foreground">
              Liste öffnen
            </Link>
          </p>
        </Card>

        <Card title="Systeme" description="Gesamt vs. in Scope">
          <p className="text-3xl font-semibold">
            {formatCount(inScopeCount)}
            <span className="text-base text-muted"> / {formatCount(systemCount)}</span>
          </p>
          <p className="mt-1 text-xs text-muted">
            <Link to="/systems" className="underline hover:text-foreground">
              Inventar öffnen
            </Link>
          </p>
        </Card>

        <Card title="Fällige Wartungen" description="Nächste 14 Tage">
          <p className="text-3xl font-semibold">
            {due.isError ? '–' : formatCount(dueCount)}
          </p>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted">
            {overdueCount > 0 && (
              <Badge tone="danger">davon {overdueCount} überfällig</Badge>
            )}
            <Link to="/maintenance/due" className="underline hover:text-foreground">
              Liste öffnen
            </Link>
          </p>
        </Card>
      </div>

      <Card title="Invoice Ninja Sync">
        <dl className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
          <div>
            <dt className="text-xs uppercase tracking-wider text-muted">Status</dt>
            <dd>
              <Badge tone={syncTone(sync.data?.last_run_status)}>
                {sync.data?.last_run_status ?? '–'}
              </Badge>
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wider text-muted">
              Verträge gesynct
            </dt>
            <dd className="font-medium">{fetched}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wider text-muted">Letzter Lauf</dt>
            <dd className="font-medium">{lastSync ? formatDate(lastSync) : '–'}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wider text-muted">Nächster Lauf</dt>
            <dd className="font-medium">{nextSync ? formatDate(nextSync) : '–'}</dd>
          </div>
        </dl>
        <p className="mt-3 text-xs text-muted">
          <Link
            to="/settings/integrations"
            className="underline hover:text-foreground"
          >
            Integrationen verwalten
          </Link>{' '}
          •{' '}
          <Link
            to="/settings/contracts/mapping"
            className="underline hover:text-foreground"
          >
            Verträge zuordnen
          </Link>
        </p>
      </Card>
    </div>
  );
}

function formatCount(n: number | null): string {
  if (n === null || n === undefined) return '…';
  return n.toLocaleString('de-AT');
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('de-AT', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

function syncTone(
  status: string | null | undefined,
): 'success' | 'warning' | 'danger' | 'neutral' {
  if (status === 'ok' || status === 'success') return 'success';
  if (status === 'error' || status === 'failed') return 'danger';
  if (status === 'running') return 'warning';
  return 'neutral';
}
